import React, { ReactElement, useEffect, useState } from 'react';
import { ReportEntity, SprintEntity, WorkDayUserEntity } from '@data';
import { Trans, useTranslation } from 'react-i18next';
import { monthAndDate, toFixedHours, useLeavingRows, useToggleState, year } from '@app/utils';
import { Switch } from '@app/components/core';
import Accordion from '@app/components/core/accordion';
import CollapseIn from '@app/components/core/collapse-in';
import SprintDayReport from '@app/components/shared/sprintDayReport';

/** All time logged in the sprint, whichever days happen to be on screen. */
export const sprintTotalHours = (days: WorkDayUserEntity[]): number =>
  toFixedHours(days.reduce((sum, day) => sum + day.totalMinutes, 0) / 60);

export interface SprintAccordionProps {
  sprint: SprintEntity;
  /** Every day within the sprint's range, reported or not. The sprint's total is this, whatever is listed. */
  days: WorkDayUserEntity[];
  /** The subset to list, when the sprint shows only some of its days. Defaults to all of them. */
  listedDays?: WorkDayUserEntity[];
  isOpen: boolean;
  todayDate: Date;
  showAddForm: (workDay: string, productIds?: string[]) => void;
  showEditForm: (report: ReportEntity) => void;
  showDeleteForm: (report: ReportEntity) => void;
  /**
   * The widest total among the sprints listed together, which sizes the total's column so every
   * "Sprint total" starts on the same line. Defaults to this sprint's own total.
   */
  columnTotalHours?: number;
  finishedDays?: string[];
  /** Days full across every product, so a product filter does not flag them as owed. */
  fullDays?: string[];
  /** First day a mark can still be taken off, as `yyyy-mm-dd`. Older marks are shown but not undone. */
  undoableFrom?: string;
  onSetFinished?: (workday: string, finished: boolean) => void;
}

const SprintAccordion = ({
  sprint,
  days,
  listedDays,
  isOpen,
  todayDate,
  showAddForm,
  showEditForm,
  showDeleteForm,
  columnTotalHours,
  finishedDays,
  fullDays,
  undoableFrom,
  onSetFinished,
}: SprintAccordionProps): ReactElement => {
  const { t } = useTranslation();
  // Mirror the accordion's own open state: Expand all acts on the day rows, and only while they are shown.
  const [open, setOpen] = useState(isOpen);
  useEffect(() => setOpen(isOpen), [isOpen]);
  const [expandAll, toggleExpandAll, setExpandAll] = useToggleState(false);
  // Collapsing takes the days with it, so the switch goes back off rather than staying on against rows
  // nobody can see.
  useEffect(() => {
    if (!open) setExpandAll(false);
  }, [open, setExpandAll]);

  // The flag rides with the row, so one held back on its way out keeps the look it left with.
  const visibleDays = (listedDays ?? days)
    .filter(day => day.reports.length > 0)
    .map(day => ({ day, finished: !!finishedDays?.includes(day.date) }));
  const { rows, isLeaving } = useLeavingRows(visibleDays, entry => entry.day.date);
  // Only inside the To report window: an older mark is shown as settled and left alone.
  const canUndo = (entry: { day: WorkDayUserEntity; finished: boolean }): boolean =>
    entry.finished && (!undoableFrom || entry.day.date >= undoableFrom);

  const totalHours = sprintTotalHours(days);
  const total = (hours: number): ReactElement => (
    <Trans
      defaults={t('sprintTotalHours', { totalHours: hours })}
      components={{ bold: <strong className="ms-1.5 font-bold tabular-nums" /> }}
    />
  );

  return (
    <Accordion
      isOpen={open}
      onOpenChange={setOpen}
      containerClassName="rounded-sm bg-card px-6"
      className="!px-0 !py-6"
      headerActions={
        <>
          <Switch
            value={expandAll}
            onChange={toggleExpandAll}
            disabled={!open}
            text={t('titles.expandAll')}
            title={open ? undefined : t('expandAllNeedsOpenSprint')}
          />
          {/* Totals differ in width from one sprint to the next ("8 hours" against "16.25 hours"), and sized
              to its own text the block walked the switch beside it left and right down the list. A hidden
              copy of the widest total on the page sets the width instead, so every card's phrase starts on
              the same line. */}
          <span className="relative ml-2 flex items-center whitespace-nowrap text-foreground">
            <span aria-hidden className="invisible">
              {total(columnTotalHours ?? totalHours)}
            </span>
            <span className="absolute inset-y-0 start-0 flex items-center">{total(totalHours)}</span>
          </span>
        </>
      }
      titleContent={
        <div className="flex flex-wrap items-center gap-2 text-start">
          <span className="mr-2 text-base font-bold text-foreground">
            {t('sprint')} {sprint.number}
          </span>
          <span className="text-base text-neutral-600 dark:text-muted-foreground">
            {monthAndDate(sprint.startDate)} – {monthAndDate(sprint.endDate)}, {year(sprint.endDate)}
          </span>
        </div>
      }
    >
      <div className="pb-6">
        {rows.map((entry, index) => (
          <CollapseIn key={entry.day.date} show={!isLeaving(entry)}>
            {/* The gap goes inside the collapsing box, or a row on its way out leaves its spacing behind. */}
            <div className={index < rows.length - 1 ? 'pb-5' : undefined}>
              <SprintDayReport
                workDay={entry.day}
                todayDate={todayDate}
                showAddForm={showAddForm}
                showEditForm={showEditForm}
                showDeleteForm={showDeleteForm}
                expandAll={expandAll}
                isFinished={entry.finished}
                isFull={fullDays?.includes(entry.day.date)}
                // Undo only: marking a day finished is To report's job, and nothing here is still owed.
                onSetFinished={
                  onSetFinished && canUndo(entry) ? finished => onSetFinished(entry.day.date, finished) : undefined
                }
              />
            </div>
          </CollapseIn>
        ))}
      </div>
    </Accordion>
  );
};

export default SprintAccordion;
